/**
 * Lifecycle Phase Card — Shows current phase from wf-0-12-001 lifecycle workflow.
 *
 * Displays the subject's current lifecycle phase (onboarding, active, exiting, closed)
 * with a progress indicator. This is a generic component — phase names come from
 * the workflow template, not hardcoded domain terms.
 *
 * CONSTRAINTS:
 * - DRR-055: Dumb frontend — phase labels from backend
 * - DRR-127: Lifecycle from wf-0-12-001 self-scheduling workflow
 * - DRR-140: Phase is derived from workflow instance current_state, not a stored field
 */

import { useMemo } from 'react';
import {
  Card,
  Group,
  Stack,
  Text,
  Badge,
  Progress,
  Skeleton,
  Alert,
  ThemeIcon,
} from '@mantine/core';
import {
  IconFocus2,
  IconCheck,
  IconPlayerPlay,
  IconLogout,
  IconArchive,
  IconAlertCircle,
} from '@tabler/icons-react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import type { WidgetProps } from '@ferp/core';

// Query to get lifecycle phase for a subject
// Uses the LIST_SUBJECTS_WITH_LIFECYCLE pattern from SubjectDashboardPage
const GET_SUBJECT_LIFECYCLE = gql`
  query GetSubjectLifecycle($subjectActorId: String!) {
    instanceApiGetInstancesBySubject(subjectActorId: $subjectActorId) {
      id
      workflowDefinitionId
      workflowName
      currentState
      status
    }
  }
`;

interface LifecycleInstance {
  id: string;
  workflowDefinitionId: string;
  workflowName?: string | null;
  currentState?: string | null;
  status?: string | null;
}

interface GetSubjectLifecycleData {
  instanceApiGetInstancesBySubject: LifecycleInstance[];
}

// Phase configuration — maps phase names to display properties
// Phase names come from wf-0-12-001 states (onboarding, active, exiting, closed)
interface PhaseConfig {
  color: string;
  icon: React.ReactNode;
  progress: number;
  description: string;
}

const PHASE_CONFIG: Record<string, PhaseConfig> = {
  onboarding: {
    color: 'blue',
    icon: <IconPlayerPlay size={16} />,
    progress: 25,
    description: 'Initial setup and orientation',
  },
  active: {
    color: 'green',
    icon: <IconCheck size={16} />,
    progress: 50,
    description: 'Active and receiving services',
  },
  exiting: {
    color: 'orange',
    icon: <IconLogout size={16} />,
    progress: 75,
    description: 'Transition and discharge planning',
  },
  closed: {
    color: 'gray',
    icon: <IconArchive size={16} />,
    progress: 100,
    description: 'Services completed',
  },
};

const DEFAULT_PHASE_CONFIG: PhaseConfig = {
  color: 'gray',
  icon: <IconFocus2 size={16} />,
  progress: 0,
  description: 'Status unknown',
};

/**
 * Displays the subject's current lifecycle phase.
 *
 * Finds the wf-0-12-001 (or similar lifecycle) workflow instance for the subject
 * and shows its current state as the lifecycle phase.
 */
export function LifecyclePhaseCard({ subjectActorId }: WidgetProps) {
  const { data, loading, error } = useQuery<GetSubjectLifecycleData>(
    GET_SUBJECT_LIFECYCLE,
    {
      variables: { subjectActorId },
      skip: !subjectActorId,
      fetchPolicy: 'cache-and-network',
    }
  );

  // Find the lifecycle workflow instance (wf-0-12-* pattern)
  const lifecycleInstance = useMemo(() => {
    const instances = data?.instanceApiGetInstancesBySubject ?? [];
    // Look for lifecycle workflow by definition ID pattern
    return instances.find(
      (i) =>
        i.workflowDefinitionId?.includes('0-12') ||
        i.workflowName?.toLowerCase().includes('lifecycle')
    );
  }, [data]);

  const currentPhase = lifecycleInstance?.currentState?.toLowerCase() ?? null;
  const phaseConfig = currentPhase
    ? PHASE_CONFIG[currentPhase] ?? DEFAULT_PHASE_CONFIG
    : DEFAULT_PHASE_CONFIG;

  if (loading && !data) {
    return (
      <Card withBorder padding="md">
        <Stack gap="sm">
          <Skeleton height={24} width={150} />
          <Skeleton height={8} />
          <Skeleton height={16} width={200} />
        </Stack>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        Failed to load lifecycle status.
      </Alert>
    );
  }

  if (!lifecycleInstance) {
    return (
      <Alert icon={<IconFocus2 size={16} />} color="blue" variant="light">
        No lifecycle workflow found for this subject.
      </Alert>
    );
  }

  return (
    <Card withBorder padding="md">
      <Stack gap="sm">
        {/* Phase header */}
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            <ThemeIcon size="md" variant="light" color={phaseConfig.color}>
              {phaseConfig.icon}
            </ThemeIcon>
            <Text fw={600} size="sm">
              Lifecycle Status
            </Text>
          </Group>
          <Badge size="lg" color={phaseConfig.color} variant="filled">
            {currentPhase?.replace(/_/g, ' ') ?? 'Unknown'}
          </Badge>
        </Group>

        {/* Progress indicator */}
        <Progress
          value={phaseConfig.progress}
          color={phaseConfig.color}
          size="sm"
          animated={currentPhase === 'active'}
        />

        {/* Phase description */}
        <Text size="xs" c="dimmed">
          {phaseConfig.description}
        </Text>

        {/* Workflow instance info */}
        <Group gap="xs">
          <Text size="xs" c="dimmed">
            {lifecycleInstance.workflowName ?? 'Lifecycle'}
          </Text>
          <Badge size="xs" variant="outline" color="gray">
            {lifecycleInstance.status?.replace(/_/g, ' ')}
          </Badge>
        </Group>
      </Stack>
    </Card>
  );
}
